import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';
import type {
    RateSnapshotListItem,
    RateSnapshotData,
    PreviewApplyResult,
    ClusterDayAssignment,
    SnapshotDayOverride,
    ClusterKind,
} from './types';
import { fetchSnapshots, previewSnapshot } from './api';
import { buildPreviewApplyResult } from './clusterUtils';
import { getRoomChain } from '@digitalopsdev/serviceops-reservations-components/dist/EditReservation/model.Rooms';
import { getProgramChain } from '@digitalopsdev/serviceops-reservations-components/dist/EditReservation/model.Programs';
import type { ReservationState, RoomBookingState, RoomBookingDate } from '@digitalopsdev/serviceops-reservations-components/dist/EditReservation/types';
import type { ProgramBookingState } from '@digitalopsdev/serviceops-reservations-components/dist/EditReservation/model.Programs';
import { ClusterAssignmentPanel } from './ClusterAssignmentPanel';
import type { RoomIsland, RoomIslandEntry, ProgramIsland, ProgramIslandEntry } from './ClusterAssignmentPanel';
import { ClusterEditorPanel } from './ClusterEditorPanel';

interface Props {
    reservationState: ReservationState;
    onClose: () => void;
    onApplied?: () => void;
}

function formatDate(d: Date): string {
    return d.toISOString().split('T')[0];
}

export function ApplyRateSnapshotModal(props: Props) {
    return (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Apply Rate Snapshot</h5>
                        <button type="button" className="btn-close" onClick={props.onClose}></button>
                    </div>
                    <ApplyRateSnapshotPanel {...props} />
                </div>
            </div>
        </div>
    );
}

export function ApplyRateSnapshotPanel({ reservationState, onClose, onApplied }: Props) {
    const [snapshots, setSnapshots] = useState<RateSnapshotListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedSnapshotId, setSelectedSnapshotId] = useState<string | null>(null);
    const [clusterAssignments, setClusterAssignments] = useState<ClusterDayAssignment[]>([]);
    const [editingClusterId, setEditingClusterId] = useState<string | null>(null);
    const [preview, setPreview] = useState<PreviewApplyResult | null>(null);
    const [applying, setApplying] = useState(false);
    const [targetCurrent, setTargetCurrent] = useState<RateSnapshotData | null>(null);

    const reservationId = reservationState.ReservationId;
    const roomTypes = reservationState.RoomTypes ?? {};
    const programTypes = reservationState.ProgramTypes ?? {};

    const roomIslands = useMemo<RoomIsland[]>(() => {
        const allRooms = reservationState.RoomBookings.toArray();
        const visited = new Set<string>();
        const result: RoomIsland[] = [];

        for (const room of allRooms) {
            if (visited.has(room.ReservationRoomId)) continue;
            const chain = getRoomChain(room.ReservationRoomId, allRooms);
            chain.forEach(r => visited.add(r.ReservationRoomId));

            const entries: RoomIslandEntry[] = chain.map(r => ({
                reservationRoomId: r.ReservationRoomId,
                roomType: r.RoomType.value,
                displayName: roomTypes[r.RoomType.value]?.Name ?? r.RoomType.value ?? 'Unknown',
                checkIn: formatDate(new Date(r.CheckIn.value)),
                checkOut: formatDate(new Date(r.CheckOut.value)),
            }));

            const startDate = entries[0].checkIn;
            const endDate = entries[entries.length - 1].checkOut;

            result.push({
                id: chain[0].ReservationRoomId,
                chain: entries,
                dateRange: `${startDate} — ${endDate}`,
                displayName: entries[0].displayName,
                startDate,
                endDate,
            });
        }
        return result;
    }, [reservationState.RoomBookings]);

    const programIslands = useMemo<ProgramIsland[]>(() => {
        const allPrograms = reservationState.ProgramBookings.toArray();
        const visited = new Set<string>();
        const result: ProgramIsland[] = [];

        for (const program of allPrograms) {
            if (visited.has(program.ReservationProgramId)) continue;
            const chain = getProgramChain(program.ReservationProgramId, allPrograms);
            chain.forEach(p => visited.add(p.ReservationProgramId));

            const entries: ProgramIslandEntry[] = chain.map(p => ({
                reservationProgramId: p.ReservationProgramId,
                programType: p.ProgramType.value,
                displayName: programTypes[p.ProgramType.value]?.Name ?? p.ProgramType.value,
                start: formatDate(new Date(p.Start.value)),
                end: formatDate(new Date(p.End.value)),
            }));

            const startDate = entries[0].start;
            const endDate = entries[entries.length - 1].end;

            result.push({
                id: chain[0].ReservationProgramId,
                chain: entries,
                dateRange: `${startDate} — ${endDate}`,
                displayName: entries[0].displayName,
                startDate,
                endDate,
            });
        }
        return result;
    }, [reservationState.ProgramBookings]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const data = await fetchSnapshots(false);
                setSnapshots(data);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    useEffect(() => {
        if (!reservationId) return;
        (async () => {
            try {
                const data = await previewSnapshot({
                    reservationId,
                    name: '(target current rates)',
                    clusters: [],
                });
                setTargetCurrent(data);
            } catch {
                setTargetCurrent(null);
            }
        })();
    }, [reservationId]);

    const selectedSnapshot = snapshots.find(s => s.rateSnapshotId === selectedSnapshotId);

    useEffect(() => {
        setClusterAssignments([]);
        setEditingClusterId(null);
    }, [selectedSnapshotId]);

    useEffect(() => {
        if (!selectedSnapshot) { setPreview(null); return; }
        setPreview(buildPreviewApplyResult(
            selectedSnapshot, targetCurrent, clusterAssignments, roomIslands, programIslands,
        ));
    }, [selectedSnapshot, targetCurrent, clusterAssignments, roomIslands, programIslands]);

    function handleApply() {
        if (!selectedSnapshotId) return;
        const snapshot = snapshots.find(s => s.rateSnapshotId === selectedSnapshotId);
        if (!snapshot) return;
        setApplying(true);
        try {
            const allRooms = reservationState.RoomBookings.toArray();
            const allPrograms = reservationState.ProgramBookings.toArray();

            for (const assignment of clusterAssignments) {
                const sourceItems = assignment.clusterKind === 'ROOM'
                    ? snapshot.data.rooms
                    : snapshot.data.programs;

                for (const day of assignment.days) {
                    const sourceItem = sourceItems[day.snapshotItemIndex];
                    if (!sourceItem) continue;
                    const sourceDay = sourceItem.dayRates[day.snapshotDayOffset];
                    if (!sourceDay) continue;

                    for (const memberId of assignment.memberIds) {
                        const booking = assignment.clusterKind === 'ROOM'
                            ? allRooms.find((r: RoomBookingState) => r.ReservationRoomId === memberId)
                            : allPrograms.find((p: ProgramBookingState) => p.ReservationProgramId === memberId);
                        if (!booking) continue;

                        const dateEntry = booking.Dates.find(
                            (d: RoomBookingDate) => formatDate(d.Date) === day.targetDate,
                        );
                        if (!dateEntry) continue;

                        dateEntry.EvaluationSessionJson?.setValue(sourceDay.evaluationSessionJson);
                        dateEntry.UseOriginalRatePlan.setValue(true);
                    }
                }
            }

            reservationState.checkPrices?.();
            onApplied?.();
            onClose();
        } finally {
            setApplying(false);
        }
    }

    function formatCurrency(amount: number) {
        return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    const editingClusterContext = useMemo(() => {
        if (!editingClusterId) return null;
        const roomIsland = roomIslands.find(i => i.id === editingClusterId);
        if (roomIsland) {
            return {
                id: roomIsland.id,
                kind: 'ROOM' as ClusterKind,
                displayName: roomIsland.displayName,
                startDate: roomIsland.startDate,
                endDate: roomIsland.endDate,
                memberIds: roomIsland.chain.map(c => c.reservationRoomId),
            };
        }
        const programIsland = programIslands.find(i => i.id === editingClusterId);
        if (programIsland) {
            return {
                id: programIsland.id,
                kind: 'PROGRAM' as ClusterKind,
                displayName: programIsland.displayName,
                startDate: programIsland.startDate,
                endDate: programIsland.endDate,
                memberIds: programIsland.chain.map(c => c.reservationProgramId),
            };
        }
        return null;
    }, [editingClusterId, roomIslands, programIslands]);

    function setDaysForEditingCluster(days: SnapshotDayOverride[]) {
        if (!editingClusterContext) return;
        const ctx = editingClusterContext;
        setClusterAssignments(prev => {
            const others = prev.filter(a => a.clusterId !== ctx.id);
            if (days.length === 0) return others;
            return [...others, { clusterId: ctx.id, clusterKind: ctx.kind, memberIds: ctx.memberIds, days }];
        });
    }

    const editingDays = clusterAssignments.find(a => a.clusterId === editingClusterId)?.days ?? [];
    const totalCoveredDays = clusterAssignments.reduce((n, c) => n + c.days.length, 0);

    return (
        <>
            <div className="modal-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                {loading ? (
                    <div className="text-center py-4 text-gray-500">Loading snapshots...</div>
                ) : snapshots.length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                        No rate snapshots available. Create one first from another reservation.
                    </div>
                ) : (
                    <>
                        <div className="mb-3">
                            <label className="form-label">Select Snapshot</label>
                            <select
                                className="form-select"
                                value={selectedSnapshotId ?? ''}
                                onChange={e => setSelectedSnapshotId(e.target.value || null)}
                            >
                                <option value="">Choose a snapshot...</option>
                                {snapshots.map(s => (
                                    <option key={s.rateSnapshotId} value={s.rateSnapshotId}>
                                        {s.name} ({s.startDate} — {s.endDate}) — {formatCurrency(s.totalGrossAmount)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {selectedSnapshot && (
                            <div className="flex flex-col lg:flex-row gap-4">
                                <div className="flex-1">
                                    <ClusterAssignmentPanel
                                        roomIslands={roomIslands}
                                        programIslands={programIslands}
                                        clusterAssignments={clusterAssignments}
                                        editingClusterId={editingClusterId}
                                        onEditCluster={setEditingClusterId}
                                        snapshot={selectedSnapshot}
                                        targetCurrent={targetCurrent}
                                        formatCurrency={formatCurrency}
                                    />
                                </div>

                                <div className="flex-1">
                                    {editingClusterContext ? (
                                        <ClusterEditorPanel
                                            snapshot={selectedSnapshot}
                                            targetCurrent={targetCurrent}
                                            cluster={editingClusterContext}
                                            days={editingDays}
                                            onDaysChange={setDaysForEditingCluster}
                                            onClose={() => setEditingClusterId(null)}
                                            formatCurrency={formatCurrency}
                                        />
                                    ) : (
                                        <ApplyPreviewPanel
                                            preview={preview}
                                            formatCurrency={formatCurrency}
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
            <div className="modal-footer">
                <button className="btn btn-outline-contrast" onClick={onClose}>Cancel</button>
                <button
                    className="btn btn-primary"
                    onClick={handleApply}
                    disabled={!selectedSnapshotId || applying || totalCoveredDays === 0}
                >
                    {applying ? 'Applying...' : 'Apply Snapshot'}
                </button>
            </div>
        </>
    );
}

// ── Preview sub-component ─────────────────────────────────────────────

interface ApplyPreviewPanelProps {
    preview: PreviewApplyResult | null;
    formatCurrency: (amount: number) => string;
}

function ApplyPreviewPanel({ preview, formatCurrency }: ApplyPreviewPanelProps) {
    return (
        <div>
            <h6 className="font-semibold mb-2">Before / After Preview</h6>
            {preview ? (
                <>
                    {preview.warnings.length > 0 && (
                        <div className="alert alert-warning py-2 text-sm mb-3">
                            {preview.warnings.map((w, i) => <div key={i}>{w}</div>)}
                        </div>
                    )}

                    {preview.clusters.length === 0 && (
                        <div className="text-sm text-gray-500 py-3">
                            No dates configured. Click <em>Edit</em> on a cluster to start picking dates.
                        </div>
                    )}

                    {preview.clusters.map((c, idx) => {
                        const totalDiff = c.days.reduce(
                            (s, d) => s + ((d.snapshotGross ?? d.currentGross) - d.currentGross),
                            0,
                        );
                        return (
                            <div key={idx} className="mb-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium">
                                        {c.clusterKind === 'ROOM' ? 'Room' : 'Program'}: {c.displayName}
                                    </span>
                                    <span className={`text-xs font-mono ${totalDiff > 0 ? 'text-green-600' : totalDiff < 0 ? 'text-red-600' : ''}`}>
                                        {totalDiff > 0 ? '+' : ''}{formatCurrency(totalDiff)}
                                    </span>
                                </div>
                                <table className="table table-sm text-xs w-full mt-1">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Source</th>
                                            <th className="text-right">Current</th>
                                            <th className="text-right">After</th>
                                            <th className="text-right">Diff</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {c.days.map(d => {
                                            const after = d.snapshotGross ?? d.currentGross;
                                            const diff = after - d.currentGross;
                                            const uncovered = d.snapshotGross === null;
                                            return (
                                                <tr key={d.date} className={uncovered ? 'text-gray-400' : ''}>
                                                    <td>{d.date}</td>
                                                    <td className="text-xs">{d.sourceLabel ?? '—'}</td>
                                                    <td className="text-right font-mono">{formatCurrency(d.currentGross)}</td>
                                                    <td className="text-right font-mono">{formatCurrency(after)}</td>
                                                    <td className={`text-right font-mono ${diff > 0 ? 'text-green-600' : diff < 0 ? 'text-red-600' : ''}`}>
                                                        {diff > 0 ? '+' : ''}{formatCurrency(diff)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        );
                    })}
                </>
            ) : null}
        </div>
    );
}
